/**
 * announcementApi.js — Announcements module data layer.
 *
 * Ported from the standalone Vite module to workplane-ui conventions:
 *   - No raw fetch + import.meta.env. All calls go through the shared
 *     api/api.js clients (hrmsGet/hrmsPost/hrmsPut, essGet/essPut), which
 *     already attach the base URL, Bearer token, X-Tenant-Code and the
 *     X-Service-Code header from sessionDB.
 *   - Paths are relative to HRMS_BASE_URL (`${API_BASE_URL}/saas/hrms`) or
 *     ESS_BASE_URL (`${API_BASE_URL}/saas/ess`), so the `/saas/hrms` and
 *     `/saas/ess` prefixes are dropped from every path here.
 *   - canonicalRoleName comes from sessionDB (login response) with a JWT
 *     fallback via lib/auth.
 *
 * Payload shape varies by recipient type:
 *   - "Department"/"Departments" -> departmentId required
 *   - "Reportees"                -> managerId required
 *   - All others                 -> no extra ID fields
 */
import { hrmsGet, hrmsPost, hrmsPut, essGet, essPut, essGetBlob } from "../api/api";
import sessionDB from "../utils/sessionDB";

// Per-endpoint service codes (carried over from the original module).
const ADD_SERVICE_CODE = "HR23A"; // Announcements Configuration > Add
// Manager lookup hits the shared employment-search endpoint (same family as
// src/services/employmentService.js's reportees search), not the Announcements
// "Add" permission — HR23A returns 403 here since it isn't scoped to that endpoint.
const EMPLOYMENT_SEARCH_SERVICE_CODE = "HR02S";
const FETCH_SERVICE_CODE = "HR23S"; // by-status list (Announcements "Search" feature)
const MY_CREATED_SERVICE_CODE = "HR23U";
const UPDATE_SERVICE_CODE = "HR23U";
const DETAIL_SERVICE_CODE = "HR23U";
const DASHBOARD_SERVICE_CODE = "ESS04S";
const DOWNLOAD_SERVICE_CODE = "ESS04DD";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Raw canonical role string for API payloads, e.g. "HR,Employee". */
function getCanonicalRoleName() {
  return sessionDB.getCanonicalRoleName() || "";
}

/**
 * Reads a File object and resolves with its raw base64 string
 * (data-URL prefix stripped).
 * @param {File} file
 * @returns {Promise<string>}
 */
export async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1] ?? "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ---------------------------------------------------------------------------
// Payload builder
// ---------------------------------------------------------------------------

export function buildAnnouncementPayload({
  announcementTitle,
  announcementBody,
  announcementType,
  recipients,
  departmentId = 0,
  managerId = 0,
  remarks = "",
  supportingDocumentBase64 = "",
  archiveDate = "",
  publishDate = "",
  announcementStatus = "",
}) {
  const base = {
    announcementTitle,
    announcementBody,
    announcementType,
    recipients,
    remarks,
    supportingDocumentBase64,
    canonicalRoleName: getCanonicalRoleName(),
    ...(archiveDate ? { archiveDate } : {}),
    ...(publishDate ? { publishDate } : {}),
    ...(announcementStatus ? { announcementStatus } : {}),
  };

  if (recipients === "Department" || recipients === "Departments") {
    return { ...base, departmentId };
  }
  if (recipients === "Reportees") {
    return { ...base, managerId };
  }
  // Managers / HR / Directors / Global: base only
  return base;
}

// ---------------------------------------------------------------------------
// Lookups (departments / managers) — fetched fresh on every call
// ---------------------------------------------------------------------------

export async function fetchDepartments() {
  const json = await hrmsGet(`/org/departments?tenantId=${sessionDB.getTenantId()}`, ADD_SERVICE_CODE);
  const raw = json?.data ?? json;
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.content)
      ? raw.content
      : Array.isArray(raw?.departments)
        ? raw.departments
        : [];
  return list.map((d) => ({
    departmentId: d.departmentId ?? d.deptId ?? d.id ?? d.department_id ?? 0,
    departmentName: d.departmentName ?? d.deptName ?? d.name ?? d.department_name ?? "",
  }));
}

export async function fetchManagers() {
  const json = await hrmsGet(
    "/current-employment/search/canonicalRoleName?searchTerm=manager&page=0&size=10",
    EMPLOYMENT_SEARCH_SERVICE_CODE
  );
  const raw = json?.data ?? json;
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.content)
      ? raw.content
      : [];
  return list.map((e) => ({
    managerId: Number(e.empId ?? e.employeeId ?? e.emp_Id ?? e.id ?? 0),
    managerName: e.name ?? e.managerName ?? `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim() ?? "",
  }));
}

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export async function submitAnnouncement({
  title,
  content,
  category = "",
  recipient,
  departmentId = 0,
  managerId = 0,
  remarks = "",
  attachedFiles = [],
  publishDate = "",
  archiveDate = "",
  announcementStatus = "",
}) {
  let supportingDocumentBase64 = "";
  if (attachedFiles.length > 0) {
    try {
      supportingDocumentBase64 = await fileToBase64(attachedFiles[0]);
    } catch {
      supportingDocumentBase64 = "";
    }
  }

  const payload = buildAnnouncementPayload({
    announcementTitle: title.trim(),
    announcementBody: content.trim(),
    announcementType: category,
    recipients: recipient,
    departmentId,
    managerId,
    remarks,
    supportingDocumentBase64,
    archiveDate,
    publishDate,
    announcementStatus,
  });

  const json = await hrmsPost("/announcements/add", payload, ADD_SERVICE_CODE);
  const announcementId =
    json?.data?.announcementId ?? json?.data?.id ?? json?.announcementId ?? null;
  const message = json?.message ?? "Announcement created successfully";
  return { announcementId, message };
}

// ---------------------------------------------------------------------------
// HR review actions (publish / send-back) and creator resubmit
// ---------------------------------------------------------------------------

export async function publishAnnouncement(announcementId, remarks = "", {
  announcementTitle = "",
  announcementBody = "",
  announcementType = "",
  recipients = "",
  departmentId = 0,
  managerId = 0,
  archiveDate = "",
  publishDate = "",
  canonicalRoleName = "",
  supportingDocumentBase64 = "",
} = {}) {
  return hrmsPut(
    `/announcements/update/${announcementId}`,
    {
      announcementStatus: "Published",
      announcementTitle,
      announcementBody,
      announcementType,
      recipients,
      departmentId,
      managerId,
      archiveDate,
      publishDate,
      canonicalRoleName: canonicalRoleName || getCanonicalRoleName(),
      remarks,
      supportingDocumentBase64,
    },
    UPDATE_SERVICE_CODE
  );
}

export async function sendBackAnnouncement(announcementId, remarks, {
  announcementTitle = "",
  announcementBody = "",
  announcementType = "",
  recipients = "",
  departmentId = 0,
  managerId = 0,
  archiveDate = "",
  publishDate = "",
  supportingDocumentBase64 = "",
} = {}) {
  return hrmsPut(
    `/announcements/update/${announcementId}`,
    {
      announcementStatus: "Required Corrections",
      announcementTitle,
      announcementBody,
      announcementType,
      recipients,
      departmentId,
      managerId,
      archiveDate,
      publishDate,
      canonicalRoleName: getCanonicalRoleName(),
      remarks,
      supportingDocumentBase64,
    },
    UPDATE_SERVICE_CODE
  );
}

/**
 * Updates an existing "Draft" announcement in place (status stays "Draft").
 * Omitting supportingDocumentBase64 (pass null) preserves the stored document.
 */
export async function updateAnnouncementDraft(announcementId, {
  announcementTitle,
  announcementBody,
  announcementType,
  recipients,
  departmentId = 0,
  managerId = 0,
  archiveDate = "",
  publishDate = "",
  remarks = "",
  supportingDocumentBase64 = null,
} = {}) {
  return hrmsPut(
    `/announcements/update/${announcementId}`,
    {
      announcementStatus: "Draft",
      announcementTitle,
      announcementBody,
      announcementType,
      recipients,
      departmentId,
      managerId,
      archiveDate,
      publishDate,
      canonicalRoleName: getCanonicalRoleName(),
      remarks,
      ...(supportingDocumentBase64 != null ? { supportingDocumentBase64 } : {}),
    },
    UPDATE_SERVICE_CODE
  );
}

/**
 * Creator re-submits a "Required Corrections" announcement back to HR.
 * Omitting supportingDocumentBase64 (pass null) preserves the stored document.
 */
export async function resubmitAnnouncement(announcementId, {
  announcementTitle,
  announcementBody,
  announcementType,
  recipients,
  departmentId = 0,
  managerId = 0,
  archiveDate = "",
  publishDate = "",
  remarks = "",
  supportingDocumentBase64 = null,
} = {}) {
  return hrmsPut(
    `/announcements/update/${announcementId}`,
    {
      announcementStatus: "Pending",
      announcementTitle,
      announcementBody,
      announcementType,
      recipients,
      departmentId,
      managerId,
      archiveDate,
      publishDate,
      canonicalRoleName: getCanonicalRoleName(),
      remarks,
      ...(supportingDocumentBase64 != null ? { supportingDocumentBase64 } : {}),
    },
    UPDATE_SERVICE_CODE
  );
}

// ---------------------------------------------------------------------------
// Display ordering + date helpers
// ---------------------------------------------------------------------------

function statusRank(status) {
  const s = (status ?? "").toLowerCase();
  if (s === "draft") return 0;
  if (s === "archived") return 2;
  return 1;
}

export function sortAnnouncementsForDisplay(list) {
  const createdMs = (x) => {
    const raw = x.createdOn ?? x.announcementCreatedOn ?? null;
    const t = raw ? new Date(raw).getTime() : NaN;
    return isNaN(t) ? 0 : t;
  };
  return [...list].sort((a, b) => {
    const rank = statusRank(a.announcementStatus) - statusRank(b.announcementStatus);
    if (rank !== 0) return rank;
    const diff = createdMs(b) - createdMs(a);
    if (diff !== 0) return diff;
    return (b.announcementId ?? 0) - (a.announcementId ?? 0);
  });
}

/** LOCAL YYYY-MM-DD for <input type="date"> (avoids UTC day-shift). */
export function toLocalISODate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export async function fetchMyCreatedAnnouncements() {
  const json = await hrmsGet(
    "/announcements/my-created?page=0&size=10&sortBy=createdOn&sortDir=DESC",
    MY_CREATED_SERVICE_CODE
  );
  const data = json?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.announcements)) return data.announcements;
  return [];
}

export async function fetchAnnouncementById(announcementId) {
  const json = await hrmsGet(`/announcements/${announcementId}`, DETAIL_SERVICE_CODE);
  return json?.data ?? json;
}

export async function fetchPublishedAnnouncements() {
  const json = await hrmsGet(
    "/announcements/by-status?status=Published&page=0&size=10&sortBy=createdOn&sortDir=DESC",
    FETCH_SERVICE_CODE
  );
  return json?.data?.content ?? (Array.isArray(json?.data) ? json.data : []);
}

export async function fetchPendingAnnouncements() {
  const json = await hrmsGet("/announcements/by-status?status=Pending", FETCH_SERVICE_CODE);
  return json?.data?.content ?? (Array.isArray(json?.data) ? json.data : []);
}

// ---------------------------------------------------------------------------
// ESS employee dashboard
// ---------------------------------------------------------------------------

/**
 * @param {"All"|"Archived"} statusFilter
 */
export async function fetchDashboardAnnouncements(statusFilter = "All") {
  const json = await essGet(
    `/announcements/dashboard?statusFilter=${statusFilter}&page=0&size=10&sortBy=acknowledgedOn&sortDir=DESC`,
    DASHBOARD_SERVICE_CODE
  );
  const data = json?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.announcements)) return data.announcements;
  return [];
}

/** Marks an announcement as opened and returns its detail data. */
export async function markAnnouncementOpened(announcementId) {
  const json = await essPut(
    `/announcements/dashboard/open/${announcementId}`,
    undefined,
    DASHBOARD_SERVICE_CODE
  );
  return json?.data ?? json;
}

/**
 * Fetches the viewer-status list for an announcement (HR/creator use).
 * Returns { content, totalPages, totalElements } where each row has
 * { empName, announcementInDashboardStatus }.
 * Status: "Opened" = read, "New" = unread.
 *
 * @param {string|number} announcementId
 * @param {number}        page  0-based page index
 */
export async function reportReadAnnouncementView(announcementId, page = 0) {
  const json = await essGet(
    `/announcements/report?announcementId=${announcementId}&statusFilter=Opened&page=${page}&size=10`,
    DASHBOARD_SERVICE_CODE
  );
  const data = json?.data ?? {};
  return {
    content: Array.isArray(data.content) ? data.content : [],
    size: data.size ?? 10,
    totalPages: data.totalPages ?? 1,
    totalElements: data.totalElements ?? 0,
  };
}


export async function reportUnreadAnnouncementView(announcementId, page = 0) {
  const json = await essGet(
    `/announcements/report?announcementId=${announcementId}&statusFilter=New&page=${page}&size=10`,
    DASHBOARD_SERVICE_CODE
  );
  const data = json?.data ?? {};
  return {
    content: Array.isArray(data.content) ? data.content : [],
    size: data.size ?? 10,
    totalPages: data.totalPages ?? 1,
    totalElements: data.totalElements ?? 0,
  };
}
// ---------------------------------------------------------------------------
// Document download (blob)
// ---------------------------------------------------------------------------

export async function downloadAnnouncementDocument(announcementId) {
  const { blob } = await essGetBlob(
    `/announcements/download/${announcementId}/document`,
    DOWNLOAD_SERVICE_CODE
  );
  return blob;
}

const EXT_BY_MIME = {
  "application/pdf": "pdf",
  "image/jpeg": "jpg",
  "image/png": "png",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
};

async function sniffDocumentExtension(blob) {
  if (EXT_BY_MIME[blob.type]) return EXT_BY_MIME[blob.type];
  const head = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
  const hex = Array.from(head, (b) => b.toString(16).padStart(2, "0")).join("");
  if (hex.startsWith("25504446")) return "pdf";  // %PDF
  if (hex.startsWith("ffd8ff")) return "jpg";
  if (hex.startsWith("89504e47")) return "png";
  if (hex.startsWith("504b0304")) return "docx"; // zip/OOXML
  if (hex.startsWith("d0cf11e0")) return "doc";  // legacy MS Office
  return "pdf";
}

export async function saveAnnouncementDocument(announcementId) {
  const blob = await downloadAnnouncementDocument(announcementId);
  const ext = await sniffDocumentExtension(blob);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `announcement-${announcementId}-document.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
